import { css, LitElement, html } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { ScopedElementsMixin } from '@open-wc/scoped-elements';
import AabIcon from '@aab/sc-aab-icon/dist/AabIcon';
import AabStatus from '@aab/sc-aab-status/dist/AabStatus';

var css_248z = css`*,:after,:before{box-sizing:border-box}.p-1{padding:8px!important}.mt-1{margin-top:8px!important}.mb-1{margin-bottom:8px!important}.d-block{display:block!important}.d-flex{display:flex!important}.align-items-center{-ms-flex-align:center!important;align-items:center!important}.position-absolute{position:absolute!important}.position-relative{position:relative!important}.text-right{text-align:right!important}.text-center{text-align:center!important}.label ::slotted(label),label{color:#3c5e5e;font-family:Roboto,Helvetica Neue,Arial,sans-serif;font-size:.875rem;font-weight:500;line-height:1.25rem}.input-group{background-clip:padding-box}.input-group ::slotted(input){background-color:#fff;border:1px solid #666;border-radius:.125rem;flex:1 1 auto;font-family:Roboto,Helvetica Neue,Arial,sans-serif;font-size:1rem;font-weight:400;line-height:1.5em;margin:auto;max-height:2.5rem;min-width:0;padding:.5em;transition:border-color .1s cubic-bezier(.4,0,.2,1);width:1%}.input-group ::slotted(input:active),.input-group ::slotted(input:focus){color:#222}.input-group ::slotted(input:active){border-color:#00857a}.input-group ::slotted(input:disabled){background-color:#f9f9f9;border-color:#ccc;color:grey}.input-group.prefixed ::slotted(input){border-bottom-left-radius:0;border-left:0;border-top-left-radius:0}.input-group:hover .input-group-prefix .input-group-prefix-text,.input-group:hover ::slotted(input),.input-group:hover.info ::slotted(input),.input-group:hover.negative ::slotted(input),.input-group:hover.positive ::slotted(input),.input-group:hover.warning ::slotted(input){border-color:#00857a}.input-group:hover ::slotted(input:disabled){border-color:#ccc}.input-group .input-icon-symbol{color:#4f706f;line-height:1.5em;padding:.5rem .5rem 0}.input-group .input-icon-symbol.disabled{filter:grayscale(100%) opacity(50%)}.input-group.icon-symbol-position-left ::slotted(input){padding-left:2.25em}.input-group.icon-symbol-position-left .input-icon-symbol{left:0}.input-group.icon-symbol-position-right ::slotted(input){padding-right:2.25em}.input-group.icon-symbol-position-right .input-icon-symbol{right:0}.input-group .input-group-prefix{display:-ms-flexbox;margin-right:-1px}.input-group .input-group-prefix .input-group-prefix-text{background-color:#f2f7f7;border:1px solid grey;border-radius:.125rem 0 0 .125rem;border-right:0;color:#4f706f;font-size:1rem;font-weight:400;line-height:1.5;transition:border-color .1s cubic-bezier(.4,0,.2,1);white-space:nowrap}.input-group.warning .input-group-prefix,.input-group.warning .input-group-prefix-text,.input-group.warning ::slotted(input){border-color:#ea580c}.input-group.negative .input-group-prefix,.input-group.negative .input-group-prefix-text,.input-group.negative ::slotted(input){border-color:#dc2626}.input-group.info .input-group-prefix,.input-group.info .input-group-prefix-text,.input-group.info ::slotted(input){border-color:#0284c7}.input-group.positive .input-group-prefix,.input-group.positive .input-group-prefix-text,.input-group.positive ::slotted(input){border-color:#65a30d}`;

/* eslint-disable no-underscore-dangle */

/**
 * @tagname aab-input
 *
 * @prop {string} value - Optional. Text value for the input element.
 * @prop {boolean} disabled - Boolean that disables the input.
 * @prop {string} prefixText - A text to prefix to the input, has a green background.
 * @prop {string} symbolText - Can be used for a currency symbol.
 * @prop {string} iconSvg - SVG string, preferably `sy_tools_search` or other system icons from @aab/sc-aab-icon-set.
 * @prop {string} iconSymbolPosition - Show the icon or the symbol on the left or right.
 * @prop {string} feedbackText - Text underneath the input, E.G. a feedback text.
 * @prop {string} feedbackState - The style of the feedback. Options are `info`, `warning`, `negative` and `positive`, default is `info`.
 * @prop {string} helperText - Message used instead of placeholder to provide the user with any essential information needed to correctly enter their data.
 * @prop {string} statusHelpId - Id for aab-status helper text component to link with input.
 * @prop {string} statusFeedbackId - Id for aab-status feedback component to link with input.
 */

class AabInput extends ScopedElementsMixin(LitElement) {
  static INPUT_HELP_ID='input-help';
  static INPUT_FEEDBACK_ID='input-feedback';

  constructor() {
    super();
    this.internals = this.attachInternals();
    this.value = '';
  }

  static get properties() {
    return {
      value: {
        type: String,
      },
      disabled: {
        type: Boolean,
      },
      prefixText: {
        type: String,
        attribute: 'prefix-text',
      },
      symbolText: {
        type: String,
        attribute: 'symbol-text',
      },
      iconSvg: {
        type: String,
        attribute: 'icon-svg',
      },
      iconSymbolPosition: {
        type: String,
        attribute: 'icon-symbol-position',
      },
      feedbackText: {
        type: String,
        attribute: 'feedback-text',
      },
      feedbackState: {
        type: String,
        attribute: 'feedback-state',
      },
      helperText: {
        type: String,
        attribute: 'helper-text',
      },
      statusHelpId: {
        type: String,
        attribute: 'status-help-id'
      },
      statusFeedbackId: {
        type: String,
        attribute: 'status-feedback-id'
      }
    };
  }

  static get scopedElements() {
    return {
      'aab-status': AabStatus,
      'aab-icon': AabIcon,
    };
  }

  static get styles() {
    return [css_248z];
  }

  static get formAssociated() {
    return true;
  }

  firstUpdated(...args) {
    super.firstUpdated(...args);
    // This ensures our element participates in the form from the beginning
    this.internals.setFormValue(this.value);
  }

  render() {
    const iconPositionClasses = classMap({
      'd-flex': true,
      'position-relative': true,
      'input-group': true,
      'icon-symbol-position-left': (this.iconSvg || this.symbolText) && this.iconSymbolPosition !== 'right',
      'icon-symbol-position-right': this.iconSymbolPosition === 'right',
      'prefixed': this.prefixText,
      [this.feedbackState]: !!this.feedbackState,
      'disabled': this.disabled,
    });

    const iconSymbolClasses = classMap({
      'd-flex': true,
      'position-absolute': true,
      'input-icon-symbol': true,
      'input-icon-symbol-disabled': this.disabled,
      'disabled': this.disabled,
    });

    return html`
      <span class="label d-block mb-1">
        <slot name="label"></slot>
      </span>
      <div class="${iconPositionClasses}">
        ${this.prefixText && html`
          <div class="input-group-prefix d-flex">
            <span class="input-group-prefix-text align-items-center text-center d-flex p-1">${this.prefixText}</span>
          </div>
          `}
        <slot></slot>

        <div class="${iconSymbolClasses}">
            <slot name="icon"></slot>
            ${
              this.iconSvg ? 
              html`<aab-icon size="3" svg=${this.iconSvg}></aab-icon>`
              : this.symbolText && html`${this.symbolText}`
            }
        </div>
      </div>
      ${this.helperText?.trim() && html`
        <aab-status
          role="none"
          class="mt-1 d-block helper-text"
          status-message=${this.helperText}
          id="${this.statusHelpId || AabInput.INPUT_HELP_ID}"
          style-type="inactive">
        </aab-status>
      `}
      ${this.feedbackText && html`
        <aab-status
          class="mt-1 d-block feedback-message"
          status-message=${this.feedbackText}
          style-type=${this.feedbackState}
          id="${this.statusFeedbackId || AabInput.INPUT_FEEDBACK_ID}"
          has-icon></aab-status>
        `}
    `;
  }
}

export { AabInput as default };
